import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import { CodeBlock, Link, Text, tokens } from '@hotjar/hotsauce';
import { Code } from '@hotjar/webapp/components/Code';
import type { Site } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import { useSiteName } from '@hotjar/site';
import { getDemoPlaceholder, getTrackingCode } from './trackingCode';

interface TrackingCodeSnippetProps {
  site: Site;
  onVerifyClick?: () => void;
}

const Paragraph = styled(Text)`
  margin-bottom: ${tokens.spacer.x2};
`;

const HeadTag = () => <Code>&lt;head&gt;</Code>;

export const TrackingCodeSnippet = ({
  site,
  onVerifyClick,
}: TrackingCodeSnippetProps): JSX.Element => {
  const siteName = useSiteName(site);
  const { data: currentUser } = useCurrentUser();
  const showDemoPlaceholder = currentUser?.isDemoUser;
  const { t } = useTranslation();

  return (
    <>
      {!showDemoPlaceholder && (
        <Paragraph as="p" textType="body2" textColor="text_default">
          {onVerifyClick ? (
            <Trans i18nKey="activation:tracking-code.installation.snippet-instructions-with-link">
              Paste the Hotjar code into the <HeadTag /> of every page you wish to track users and
              collect feedback. And then{' '}
              <Link
                as="button"
                kind="inline"
                onClick={() => onVerifyClick()}
                data-testid="verify-tracking-code-link"
              >
                verify
              </Link>{' '}
              your installation.
            </Trans>
          ) : (
            <Trans i18nKey="activation:tracking-code.installation.snippet-instructions">
              Paste the Hotjar code into the <HeadTag /> of every page you wish to track users and
              collect feedback. And then verify your installation.
            </Trans>
          )}
        </Paragraph>
      )}

      {showDemoPlaceholder && (
        <Paragraph as="p" textType="body2" textColor="text_default">
          <Trans i18nKey="activation:tracking-code.installation.demo-account-instructions">
            Here’s where Hotjar provides a unique tracking code for every site. To set up Hotjar on
            your own site and get your unique tracking code{' '}
            <Link
              isExternal
              href="/register?c=a86dd6a983b5480dabf306c8f1f95b26&signup_source=demo-account-tracking-code"
              kind="inline"
            >
              head here
            </Link>
            .
          </Trans>
        </Paragraph>
      )}

      <CodeBlock
        copyText={t('activation:tracking-code.installation.copy-btn-text')}
        copyFeedback={t('activation:tracking-code.installation.copy-btn-feedback')}
        content={
          showDemoPlaceholder ? getDemoPlaceholder(siteName) : getTrackingCode(siteName, site.id)
        }
        allowCopy
      />
    </>
  );
};
